import {
  DefaultServiceMessages,
  Route,
  ServiceSettings,
  UpdateServiceSettings,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"
import {
  ADDON_ORDER_REMINDER_MESSAGE_QUERY_KEY,
  BIKE_DONE_REMINDER_MESSAGE_QUERY_KEY,
  IS_DONE_MESSAGE_QUERY_KEY,
  PRICE_PROPOSAL_REMINDER_MESSAGE_QUERY_KEY,
  RECEPTION_CONFIRMATION_MESSAGE_QUERY_KEY,
} from "./job-queries"
import { MESSAGE_VARIABLE_QUERY_KEY } from "./message-variable-queries"

async function fetchServiceSettings(storeId: string): Promise<ServiceSettings> {
  return await request<ServiceSettings>({
    url: populateRoute(
      Route.SERVICE_SETTINGS_CONTROLLER__FIND_ONE_BY_STORE_ID,
      storeId,
    ),
    method: "GET",
  })
}

async function fetchDefaultMessages(): Promise<DefaultServiceMessages> {
  return await request<DefaultServiceMessages>({
    url: Route.SERVICE_SETTINGS_CONTROLLER__FIND_DEFAULT_MESSAGES,
    method: "GET",
  })
}

async function updateServiceSettings(
  storeId: string,
  data: UpdateServiceSettings,
) {
  return await request<ServiceSettings>({
    url: populateRoute(Route.SERVICE_SETTINGS_CONTROLLER__UPDATE, storeId),
    method: "PATCH",
    data,
  })
}

export const useUpdateServiceSettings = (storeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateServiceSettings) =>
      updateServiceSettings(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["service-settings", storeId],
      })

      // These texts are built from the settings just saved. Their dialogs are
      // closed while settings are edited, so a default invalidation would only
      // mark them stale and the next open would prefill the old text from
      // cache. Refetching them now means the dialog opens with the new text.
      // The reminder dialog keeps no copy of its texts (see
      // send-reminder-dialog), but the chat composer does.
      for (const key of [
        IS_DONE_MESSAGE_QUERY_KEY,
        RECEPTION_CONFIRMATION_MESSAGE_QUERY_KEY,
        PRICE_PROPOSAL_REMINDER_MESSAGE_QUERY_KEY,
        ADDON_ORDER_REMINDER_MESSAGE_QUERY_KEY,
        BIKE_DONE_REMINDER_MESSAGE_QUERY_KEY,
      ]) {
        queryClient.invalidateQueries({
          queryKey: [key],
          refetchType: "all",
        })
      }

      // The greeting just saved is one of the resolved variable values too, so
      // the "#" picker would otherwise insert the old one until its refetch lands.
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_VARIABLE_QUERY_KEY, "resolved"],
        refetchType: "all",
      })
    },
  })
}

const serviceSettingsQueries = {
  detail: (storeId: string) =>
    queryOptions({
      queryKey: ["service-settings", storeId],
      queryFn: () => fetchServiceSettings(storeId),
    }),
  defaultMessages: () =>
    queryOptions({
      queryKey: ["service-settings", "default-messages"],
      queryFn: fetchDefaultMessages,
    }),
}

export default serviceSettingsQueries
